
import React from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card } from "@/components/ui/card";
import { X, Filter, Clock, CreditCard, ArrowUpDown, ArrowUp, ArrowDown, RotateCcw } from "lucide-react";
import { useWorkoutTypes } from "@/hooks/useWorkoutTypes";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";
import { ClientsSortField, ClientsSortDirection, CLIENTS_SORT_OPTIONS } from "@/constants/clientsPagination";

interface ClientFiltersProps {
  selectedWorkoutType: string | null;
  onWorkoutTypeChange: (workoutTypeId: string | null) => void;
  showSoonExpire: boolean;
  onSoonExpireChange: (show: boolean) => void;
  showUnpaidOnly: boolean;
  onUnpaidOnlyChange: (show: boolean) => void;
  sortField: ClientsSortField;
  sortDirection: ClientsSortDirection;
  onSortChange: (field: ClientsSortField, direction: ClientsSortDirection) => void;
  onResetFilters: () => void;
  isLoading?: boolean;
  forceExpanded?: boolean;
}

const ClientFilters: React.FC<ClientFiltersProps> = ({
  selectedWorkoutType,
  onWorkoutTypeChange,
  showSoonExpire,
  onSoonExpireChange,
  showUnpaidOnly,
  onUnpaidOnlyChange,
  sortField,
  sortDirection,
  onSortChange,
  onResetFilters,
  isLoading = false,
  forceExpanded = false,
}) => {
  const { t } = useTranslation();
  const { workoutTypes } = useWorkoutTypes();
  const isMobile = useIsMobile();

  const selectedWorkoutTypeName = workoutTypes.find(
    (wt) => wt.id === selectedWorkoutType
  )?.name;

  const hasActiveFilters =
    selectedWorkoutType !== null || showSoonExpire || showUnpaidOnly;

  const handleSortFieldChange = (newField: ClientsSortField) => {
    onSortChange(newField, sortDirection);
  };

  const handleSortDirectionToggle = () => {
    const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    onSortChange(sortField, newDirection);
  };

  const getSortIcon = () => {
    if (sortDirection === 'asc') {
      return <ArrowUp className="h-4 w-4" />;
    } else if (sortDirection === 'desc') {
      return <ArrowDown className="h-4 w-4" />;
    }
    return <ArrowUpDown className="h-4 w-4" />;
  };

  const FiltersContent = () => (
    <div className="space-y-4">
      {/* Filters Section */}
      <div className="flex flex-wrap gap-2 border-b pb-3">
        <Select
          value={selectedWorkoutType || "all"}
          onValueChange={(value) =>
            onWorkoutTypeChange(value === "all" ? null : value)
          }
        >
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder={t('clients.filters.workoutTypePlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('clients.filters.allWorkoutTypes')}</SelectItem>
            {workoutTypes.map((workoutType) => (
              <SelectItem key={workoutType.id} value={workoutType.id}>
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: workoutType.color }}
                  />
                  {workoutType.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant={showSoonExpire ? "default" : "outline"}
          size="sm"
          onClick={() => onSoonExpireChange(!showSoonExpire)}
          className="flex items-center gap-2"
        >
          <Clock className="h-4 w-4" />
          <span className="greek-text">{t('clients.filters.soonExpire')}</span>
        </Button>

        <Button
          variant={showUnpaidOnly ? "default" : "outline"}
          size="sm"
          onClick={() => onUnpaidOnlyChange(!showUnpaidOnly)}
          className="flex items-center gap-2"
        >
          <CreditCard className="h-4 w-4" />
          <span className="greek-text">{t('clients.filters.unpaidOnly')}</span>
        </Button>

        <div className="flex flex-wrap gap-1">
          {selectedWorkoutType && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <span className="greek-text">{selectedWorkoutTypeName}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 hover:bg-transparent"
                onClick={() => onWorkoutTypeChange(null)}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}
          
          {showSoonExpire && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <span className="greek-text">{t('clients.filters.soonExpire')}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 hover:bg-transparent"
                onClick={() => onSoonExpireChange(false)}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}

          {showUnpaidOnly && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <span className="greek-text">{t('clients.filters.unpaidOnly')}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 hover:bg-transparent"
                onClick={() => onUnpaidOnlyChange(false)}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}
        </div>
      </div>
      {/* Sorting Section */}
      <div className="flex flex-wrap items-center gap-3 pt-3">
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {t('common.sort.sortBy')}:
        </span>
        
        <Select
          value={sortField}
          onValueChange={handleSortFieldChange}
          disabled={isLoading}
        >
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CLIENTS_SORT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {t(option.label)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          size="sm"
          onClick={handleSortDirectionToggle}
          disabled={isLoading}
          className="px-3"
          title={t(`common.sort.${sortDirection === 'asc' ? 'ascending' : 'descending'}`)}
        >
          {getSortIcon()}
        </Button>
      </div>
    </div>
  );

  // On mobile, use collapsible accordion
  if (isMobile) {
    return (
      <Card className="mb-4 w-full">
        <Accordion 
          type="single" 
          collapsible 
          className="w-full" 
          defaultValue={forceExpanded ? "filters" : undefined}
        >
          <AccordionItem value="filters" className="border-none">
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4" />
                  <span className="text-sm font-medium greek-text">{t('clients.filters.title')}</span>
                  {hasActiveFilters && (
                    <div className="h-2 w-2 bg-blue-500 rounded-full" />
                  )}
                </div>
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onResetFilters();
                    }}
                    className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                    title={t('clients.filters.reset')}
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <FiltersContent />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </Card>
    );
  }

  // On desktop, show filters directly
  return (
    <div className="space-y-3 mb-4">
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium greek-text">{t('clients.filters.title')}</span>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onResetFilters}
            className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
            title={t('clients.filters.reset')}
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            <span className="greek-text">{t('clients.filters.reset')}</span>
          </Button>
        )}
      </div>
      <FiltersContent />
    </div>
  );
};

export default ClientFilters;
